import type { Task as FhirTask } from "fhir/r5";

import {
  CRM_CONTRACT_TIER_SYSTEM,
  CRM_CONTRACT_TIERS,
} from "../../contracts/constants/crmContractTiers";
import {
  CRM_TASK_BATCH_NOTIFICATION_CODE,
  CRM_TASK_BATCH_NOTIFICATION_CODE_FALLBACK,
  CRM_TASK_INPUT_REQUESTED_COUNT_CODE,
  CRM_TASK_OUTPUT_SUCCESS_COUNT_CODE,
  CRM_TASK_OUTPUT_SKIPPED_COUNT_CODE,
  PURCHASED_CREDITS_EXTENSION_URL,
  PURCHASED_CREDITS_QUANTITY_EXTENSION_URL,
} from "../constants/crmMessageFranchiseFhirConstants";
import type { CrmMessageBatchHistoryItem } from "../types";

/**
 * Funções puras para derivar números de tela a partir de Contract / Task / PaymentNotice.
 * Mantém a mesma regra do componente legado `CRMQuickStats` (FluxZap).
 */

/**
 * Extrai recursos de um Bundle FHIR (ignora entries sem resource).
 */
export function extractResourcesFromBundle<T extends fhir5.Resource>(
  bundle: fhir5.Bundle | undefined,
): T[] {
  if (!bundle?.entry?.length) return [];
  const result: T[] = [];
  for (const entry of bundle.entry) {
    const resource = entry.resource as T | undefined;
    if (resource?.resourceType) {
      result.push(resource);
    }
  }
  return result;
}

/**
 * "Disponíveis" no legado `CRMQuickStats`: primeiro termo do Contract.
 * (Diferente de `useAvailableMessages`, que usa o termo `maxRequest`.)
 */
export function getAvailableMessagesFromContractFirstTerm(
  contract: fhir5.Contract | null | undefined,
): number {
  const raw = contract?.term?.[0]?.identifier?.value;
  if (raw === undefined || raw === null || raw === "") return 0;
  const n = Number(raw);
  return Number.isFinite(n) ? n : 0;
}

const MAX_REQUEST_TERM_SYSTEM = "maxRequest";

/** Saldo enviável em pacotes pagos — termo `maxRequest` (legado `useAvailableMessages`). */
export function getMaxRequestMessagesFromContract(
  contract: fhir5.Contract | null | undefined,
): number {
  if (!contract?.term?.length) return 0;

  const maxRequestTerm = contract.term.find((term) =>
    term.identifier?.system?.includes(MAX_REQUEST_TERM_SYSTEM),
  );
  const maxRequestRaw = maxRequestTerm?.identifier?.value;
  if (maxRequestRaw === undefined || maxRequestRaw === "") return 0;

  const maxRequest = Number(maxRequestRaw);
  return Number.isFinite(maxRequest) && maxRequest > 0 ? maxRequest : 0;
}

export function getCrmContractTier(
  contract: fhir5.Contract | null | undefined,
): string | null {
  if (!contract?.identifier?.length) return null;
  const bySystem = contract.identifier.find(
    (identifier) => identifier.system === CRM_CONTRACT_TIER_SYSTEM,
  );
  return bySystem?.value?.trim() || contract.identifier[0]?.value?.trim() || null;
}

/** Exibição de “pool” no painel: maxRequest quando existir; senão primeiro termo. */
export function getAvailableMessagesFromContract(
  contract: fhir5.Contract | null | undefined,
): number {
  if (!contract) return 0;

  const maxRequest = getMaxRequestMessagesFromContract(contract);
  if (maxRequest > 0) return maxRequest;

  return getAvailableMessagesFromContractFirstTerm(contract);
}

/**
 * Saldo restante para envio (paridade com `useAvailableMessages` + trial sem maxRequest):
 * 1. Termo `maxRequest` quando presente — saldo enviável direto (crm20/crm50/crm100).
 * 2. Senão, trial: pool do primeiro termo (ex.: 20) − Tasks concluídas.
 */
export function getRemainingMessagesForSendFromContract(
  contract: fhir5.Contract | null | undefined,
  totalMessagesUsed: number,
): number {
  if (!contract) return 0;

  const maxRequestBalance = getMaxRequestMessagesFromContract(contract);
  if (maxRequestBalance > 0) {
    return maxRequestBalance;
  }

  const pool = getAvailableMessagesFromContractFirstTerm(contract);
  return Math.max(0, pool - totalMessagesUsed);
}

export interface ResolvedCrmContractForSend {
  contract: fhir5.Contract;
  totalMessagesAvailable: number;
  totalMessagesUsed: number;
  remainingMessages: number;
}

/** 1) crm20 com saldo → 2) crm50/crm100 com saldo → 3) null (comprar pacote). */
export function resolveActiveCrmContractForSend(params: {
  trialContract: fhir5.Contract | null;
  paidContract: fhir5.Contract | null;
  totalMessagesUsed: number;
}): ResolvedCrmContractForSend | null {
  const { trialContract, paidContract, totalMessagesUsed } = params;

  if (trialContract?.id) {
    const remainingMessages = getRemainingMessagesForSendFromContract(
      trialContract,
      totalMessagesUsed,
    );
    if (remainingMessages > 0) {
      return {
        contract: trialContract,
        totalMessagesAvailable:
          getAvailableMessagesFromContractFirstTerm(trialContract),
        totalMessagesUsed,
        remainingMessages,
      };
    }
  }

  if (paidContract?.id) {
    const remainingMessages = getRemainingMessagesForSendFromContract(
      paidContract,
      totalMessagesUsed,
    );
    if (remainingMessages > 0) {
      return {
        contract: paidContract,
        totalMessagesAvailable: getAvailableMessagesFromContract(paidContract),
        totalMessagesUsed,
        remainingMessages,
      };
    }
  }

  return null;
}

export function pickFallbackCrmContractForEmptyBalance(params: {
  trialContract: fhir5.Contract | null;
  paidContract: fhir5.Contract | null;
}): fhir5.Contract | null {
  return params.trialContract ?? params.paidContract;
}

/** Task de envio em lote (FluxZap) — reutilizado pelo módulo communications. */
export function isCrmBatchNotificationTask(task: FhirTask): boolean {
  return Boolean(
    task.code?.coding?.some(
      (coding) =>
        coding.code === CRM_TASK_BATCH_NOTIFICATION_CODE ||
        coding.code === CRM_TASK_BATCH_NOTIFICATION_CODE_FALLBACK,
    ),
  );
}

function getCompletedSuccessCount(task: FhirTask): number {
  if (task.status !== "completed") return 0;
  const successOutput = task.output?.find(
    (output) =>
      output.type?.coding?.[0]?.code === CRM_TASK_OUTPUT_SUCCESS_COUNT_CODE,
  );
  return typeof successOutput?.valueInteger === "number"
    ? successOutput.valueInteger
    : 0;
}

function readTaskInputIntegerValue(task: FhirTask, code: string): number {
  const value = task.input?.find((input) => input.type?.coding?.[0]?.code === code)
    ?.valueInteger;
  return typeof value === "number" ? value : 0;
}

function readTaskOutputIntegerValue(task: FhirTask, code: string): number {
  const value = task.output?.find((output) => output.type?.coding?.[0]?.code === code)
    ?.valueInteger;
  return typeof value === "number" ? value : 0;
}

/**
 * Soma mensagens já enviadas (Tasks CRM concluídas com SUCCESS_COUNT).
 * Paridade com `CRMQuickStats.stats.sentMessages`.
 */
export function countSentMessagesFromCrmTasks(tasks: FhirTask[]): number {
  return tasks.reduce((acc, task) => {
    if (!isCrmBatchNotificationTask(task)) return acc;
    return acc + getCompletedSuccessCount(task);
  }, 0);
}

function mapBatchStatus(
  taskStatus: FhirTask["status"],
): Pick<CrmMessageBatchHistoryItem, "status" | "statusLabel"> {
  if (taskStatus === "completed") {
    return { status: "completed", statusLabel: "Finalizado" };
  }
  if (
    taskStatus === "requested" ||
    taskStatus === "ready" ||
    taskStatus === "in-progress"
  ) {
    return { status: "inProgress", statusLabel: "Em andamento" };
  }
  if (
    taskStatus === "failed" ||
    taskStatus === "cancelled" ||
    taskStatus === "rejected"
  ) {
    return { status: "failed", statusLabel: "Falha" };
  }
  return { status: "other", statusLabel: "Outro status" };
}

export function buildCrmBatchHistoryFromTasks(
  tasks: FhirTask[],
  limit: number = 8,
): CrmMessageBatchHistoryItem[] {
  const taskHistory = tasks
    .filter(isCrmBatchNotificationTask)
    .map((task) => {
      const authoredAt = task.authoredOn ?? task.meta?.lastUpdated ?? "";
      const authoredDate = authoredAt ? new Date(authoredAt) : null;
      const authoredTimestamp =
        authoredDate != null && !Number.isNaN(authoredDate.getTime())
          ? authoredDate.getTime()
          : 0;
      const dateLabel =
        authoredDate != null && authoredTimestamp > 0
          ? authoredDate.toLocaleString("pt-BR", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
          })
          : "Data indisponível";

      const status = mapBatchStatus(task.status);

      return {
        id: task.id ?? `${task.identifier?.[0]?.value ?? "task"}-${authoredTimestamp}`,
        dateLabel,
        requestedCount: readTaskInputIntegerValue(
          task,
          CRM_TASK_INPUT_REQUESTED_COUNT_CODE,
        ),
        sentCount: readTaskOutputIntegerValue(task, CRM_TASK_OUTPUT_SUCCESS_COUNT_CODE),
        failedCount: readTaskOutputIntegerValue(task, CRM_TASK_OUTPUT_SKIPPED_COUNT_CODE),
        status: status.status,
        statusLabel: status.statusLabel,
        authoredTimestamp,
      };
    })
    .sort((a, b) => b.authoredTimestamp - a.authoredTimestamp);

  return taskHistory.slice(0, Math.max(1, limit)).map(({ authoredTimestamp, ...item }) => item);
}

function getPurchasedCreditsQuantity(payment: fhir5.PaymentNotice): number {
  const hasPurchasedCredits = payment.extension?.some(
    (ext) => ext.url === PURCHASED_CREDITS_EXTENSION_URL,
  );
  if (!hasPurchasedCredits) return 0;

  const purchasedCredits = payment.extension?.find(
    (ext) => ext.url === PURCHASED_CREDITS_EXTENSION_URL,
  );
  const quantity = purchasedCredits?.extension?.find(
    (ext) => ext.url === PURCHASED_CREDITS_QUANTITY_EXTENSION_URL,
  )?.valuePositiveInt;

  return typeof quantity === "number" ? quantity : 0;
}

/**
 * Soma créditos contratados via PaymentNotice (extensão purchased-credits).
 * Paridade com `CRMQuickStats.stats.totalMessages`.
 */
export function countTotalContractedFromPaymentNotices(
  payments: fhir5.PaymentNotice[],
): number {
  return payments.reduce(
    (acc, payment) => acc + getPurchasedCreditsQuantity(payment),
    0,
  );
}

/**
 * Taxa exibida no legado: enviadas / disponíveis, limitada a 0–100%.
 * (`CRMQuickStats.utilizationRate`.)
 */
export function computeCrmUtilizationRateInPercentage(
  availableMessages: number,
  sentMessages: number,
): number {
  if (availableMessages <= 0) return 0;
  return Math.min(100, Math.max(0, (sentMessages / availableMessages) * 100));
}
